import { useFlags } from '@atlaskit/flag';
import { SettingsVehicleChecksApiService } from 'core/api';
import { useDialog } from 'core/providers/DialogProvider';
import SharedButton from 'shared/components/buttons/button';
import PageHeader from 'shared/components/page-header/page-header';
import SharedTable from 'shared/components/table/table';
import {
  ISharedTableColumn,
  ISharedTableCustomCellTemplate,
  ISharedTableRow,
} from 'shared/components/table/table-interface';
import ConfirmationDialog from 'modules/dialogs/confirmation-dialog';
import { useCollection } from 'core/hooks/useCollection';
import { deleteItem, deleteItems } from 'core/utilities/api-wrapper';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import Checkbox from '@atlaskit/checkbox';
import SharedFilters, { Filter, SelectedValue } from 'shared/components/filters/filters';
import { PageElement } from 'core/utilities/interface-helpers';
import { QueryConstraint, Timestamp, where } from 'firebase/firestore';
import { IButton } from 'shared/components/buttons/button-interface';
import { ISettingsVehicleCheckDao } from 'core/api/types/settings-vehicle-checks.interface';
import { VehicleCheckDefaultOptions } from 'core/constants/vehicle-check-default-options';
import { Frequency } from 'core/constants/frequency';
import AddEditSettingsVehicleCheckDialog from './add-edit-settings-vehicle-checks-dialog';

const SettingsVehicleChecksTableColumns: ISharedTableColumn[] = [
  { label: '', key: 'select', templateId: 'select', width: 1, isCompact: true },
  { label: 'Name', key: 'name', templateId: 'text', width: 10 },
  { label: 'Status', key: 'active', templateId: 'boolean', width: 10 },
  { label: '', key: 'action', templateId: 'action', width: 10 },
];

const VehicleCheckSettingsList = () => {
  const dialog = useDialog();
  const flags = useFlags();

  const [filterQuery, setFilterQuery] = useState<SelectedValue[]>([]);
  const [constraints, setConstraints] = useState<QueryConstraint[]>([]);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [fullTableRows, setFullTableRows] = useState<ISharedTableRow<ISettingsVehicleCheckDao>[]>([]);

  const { tableRows, loading, loadMore, hasMore, loadingMore } = useCollection<ISettingsVehicleCheckDao>({
    apiService: SettingsVehicleChecksApiService,
    constraints,
  });

  useEffect(() => {
    setSelectedRows([]);
  }, [tableRows]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const newConstraints = filterQuery
        .filter((filter) => filter.value !== '' && !filter.id.includes('.'))
        .map((filter) => {
          const value = filter.value === 'true' ? true : filter.value === 'false' ? false : filter.value;
          return where(filter.id, '==', value);
        });

      setConstraints(newConstraints);
    }, 500);
    return () => clearTimeout(timer);
  }, [filterQuery]);

  const handleFilterChange = useCallback(
    (values: SelectedValue[]) => {
      const currentSerialized = JSON.stringify(filterQuery.sort((a, b) => a.id.localeCompare(b.id)));
      const newSerialized = JSON.stringify(values.sort((a, b) => a.id.localeCompare(b.id)));

      if (currentSerialized !== newSerialized) {
        setFilterQuery(values);
      }
    },
    [filterQuery]
  );

  const rowChecked = (event: ChangeEvent<HTMLInputElement>, uid: string) => {
    if (event.target.checked) {
      setSelectedRows((prevState) => [...prevState, uid]);
    } else {
      setSelectedRows((prevState) => prevState.filter((selectedUid) => selectedUid !== uid));
    }
  };

  const selectCellTemplate = (settingsVehicleCheck: ISettingsVehicleCheckDao) => {
    if (
      settingsVehicleCheck.name === 'Mileage' ||
      settingsVehicleCheck.name === 'Tyres' ||
      settingsVehicleCheck.name === 'Damage'
    ) {
      return null;
    }

    return (
      <Checkbox
        isChecked={selectedRows.includes(settingsVehicleCheck.uid)}
        onChange={(e) => rowChecked(e, settingsVehicleCheck.uid)}
      />
    );
  };

  const actionCellTemplate = (settingsVehicleCheck: ISettingsVehicleCheckDao) => {
    return (
      <div className='w-full flex justify-end space-x-2'>
        <SharedButton
          onClick={() =>
            dialog?.openDialog(<AddEditSettingsVehicleCheckDialog settingsVehicleCheck={settingsVehicleCheck} />)
          }
          type='button'
          appearance='subtle'
          spacing='compact'
          label='Update'
        />
        {settingsVehicleCheck.name !== 'Mileage' &&
          settingsVehicleCheck.name !== 'Tyres' &&
          settingsVehicleCheck.name !== 'Damage' && (
            <SharedButton
              onClick={() =>
                dialog?.openDialog(
                  <ConfirmationDialog
                    title='Delete vehicle check'
                    textContent='Are you sure you want to delete this vehicle check? This action cannot be undone.'
                    action={() => deleteItem(SettingsVehicleChecksApiService, settingsVehicleCheck, flags, dialog)}
                    buttonLabel='Delete Vehicle Check'
                  />
                )
              }
              type='button'
              appearance='subtle'
              spacing='compact'
              label='Delete'
            />
          )}
      </div>
    );
  };

  const customTemplates: ISharedTableCustomCellTemplate[] = [
    { template: selectCellTemplate, id: 'select' },
    {
      template: actionCellTemplate,
      id: 'action',
    },
    { template: selectCellTemplate, id: 'select' },
  ];

  const headerActions: PageElement[] = [
    ...(selectedRows.length
      ? [
          {
            key: 'deleteSettingsVehicleChecks',
            element: (
              <SharedButton
                onClick={() =>
                  dialog?.openDialog(
                    <ConfirmationDialog
                      title='Delete vehicle checks'
                      textContent='Are you sure you want to delete these vehicle checks? This action cannot be undone.'
                      action={() => deleteItems(SettingsVehicleChecksApiService, selectedRows, flags, dialog)}
                      buttonLabel='Delete Vehicle Checks'
                    />
                  )
                }
                type='button'
                appearance='danger'
                label='Delete Vehicle Checks'
              />
            ),
          },
        ]
      : [
          {
            key: 'addSettingsVehicleCheck',
            element: (
              <SharedButton
                onClick={() => dialog?.openDialog(<AddEditSettingsVehicleCheckDialog />)}
                type='button'
                appearance='primary'
                label='Add Vehicle Check'
              />
            ),
          },
        ]),
  ];

  const filters: Filter[] = [
    {
      id: 'active',
      label: 'Active',
      options: [
        { value: 'true', label: 'Active' },
        { value: 'false', label: 'Inactive' },
      ],
    },
  ];

  const loadMoreButton: IButton = {
    onClick: () => loadMore(),
    type: 'submit',
    appearance: 'subtle',
    label: 'Load more',
    isLoading: loadingMore,
  };

  useEffect(() => {
    const mileage: ISharedTableRow<ISettingsVehicleCheckDao>[] | undefined = tableRows.find(
      (row) => row.data.name === 'Mileage'
    )
      ? []
      : [
          {
            key: '0',
            data: {
              uid: '0',
              name: 'Mileage',
              active: false,
              options: [],
              requireComment: [],
              frequency: [Frequency.DAILY],
              created: {
                at: Timestamp.now(),
                by: {
                  uid: '0',
                  fullName: 'Mileage',
                },
              },
              updated: {
                at: Timestamp.now(),
                by: {
                  uid: '0',
                  fullName: 'Mileage',
                },
              },
            },
          },
        ];

    const tyres: ISharedTableRow<ISettingsVehicleCheckDao>[] = tableRows.find((row) => row.data.name === 'Tyres')
      ? []
      : [
          {
            key: '1',
            data: {
              uid: '0',
              name: 'Tyres',
              active: false,
              options: VehicleCheckDefaultOptions.map((option) => ({ value: option.value, label: option.label })),
              requireComment: [],
              frequency: [Frequency.DAILY],
              created: {
                at: Timestamp.now(),
                by: {
                  uid: '0',
                  fullName: 'System',
                },
              },
              updated: {
                at: Timestamp.now(),
                by: {
                  uid: '0',
                  fullName: 'System',
                },
              },
            },
          },
        ];

    const damage: ISharedTableRow<ISettingsVehicleCheckDao>[] = tableRows.find((row) => row.data.name === 'Damage')
      ? []
      : [
          {
            key: '2',
            data: {
              uid: '0',
              name: 'Damage',
              active: false,
              options: [],
              requireComment: [],
              frequency: [Frequency.DAILY],
              created: {
                at: Timestamp.now(),
                by: {
                  uid: '0',
                  fullName: 'System',
                },
              },
              updated: {
                at: Timestamp.now(),
                by: {
                  uid: '0',
                  fullName: 'System',
                },
              },
            },
          },
        ];

    setFullTableRows([...mileage, ...tyres, ...damage, ...tableRows]);
  }, [tableRows]);

  return (
    <>
      <PageHeader title='Vehicle Checks' actions={headerActions} />
      <SharedFilters filters={filters} onFilterValueChange={handleFilterChange} />
      <SharedTable
        columns={SettingsVehicleChecksTableColumns}
        customTemplates={customTemplates}
        rows={fullTableRows}
        isLoading={loading}
        loadMoreButton={hasMore ? loadMoreButton : undefined}
      />
    </>
  );
};

export default VehicleCheckSettingsList;
